import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import PageSEO from "@/components/base/PageSEO";

const timeSlots = ["10:00", "10:30", "11:00", "11:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30"];

const services = [
  { id: "try-on", label: "商品試穿", desc: "挑選喜愛商品，由造型顧問協助試穿與搭配" },
  { id: "styling", label: "一對一造型諮詢", desc: "專業顧問根據您的風格與場合提供整體穿搭建議" },
  { id: "gift", label: "禮品挑選服務", desc: "為親友挑選服飾禮物，提供專業建議與包裝" },
];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

export default function Appointment() {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [selectedService, setSelectedService] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const monthNames = ["1月","2月","3月","4月","5月","6月","7月","8月","9月","10月","11月","12月"];
  const weekDays = ["日","一","二","三","四","五","六"];

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentYear(y => y - 1); setCurrentMonth(11); }
    else setCurrentMonth(m => m - 1);
    setSelectedDate("");
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentYear(y => y + 1); setCurrentMonth(0); }
    else setCurrentMonth(m => m + 1);
    setSelectedDate("");
  };

  const isBeforeToday = (day: number) => {
    const d = new Date(currentYear, currentMonth, day);
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return d < t;
  };
  const isSunday = (day: number) => new Date(currentYear, currentMonth, day).getDay() === 0;

  const formatDate = (day: number) =>
    `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    if (!selectedDate || !selectedTime || !selectedService) {
      setError("請選擇預約日期、時段及服務類型。");
      return;
    }
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append("name", (form.elements.namedItem("name") as HTMLInputElement).value);
    data.append("phone", (form.elements.namedItem("phone") as HTMLInputElement).value);
    data.append("email", (form.elements.namedItem("email") as HTMLInputElement).value);
    data.append("service", selectedService);
    data.append("date", selectedDate);
    data.append("time", selectedTime);
    data.append("note", (form.elements.namedItem("note") as HTMLTextAreaElement).value);
    try {
      await fetch("https://readdy.ai/api/form/d7f3jasq2rhf4jj5qd9g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setError("送出失敗，請稍後再試或直接加入 LINE 聯繫我們。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <PageSEO
        title="預約試穿｜台中設計師女裝Outlet 線上預約試穿服務"
        description="台中設計師女裝Outlet提供線上預約試穿服務，選擇商品試穿、一對一造型諮詢或禮品挑選，專業服務讓您輕鬆找到最適合的女裝。"
        keywords="台中女裝預約試穿,設計師品牌造型諮詢,台中服飾預約,Outlet試穿服務"
        canonical="https://www.example.com/appointment"
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[42vh] min-h-[320px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20taiwanese%20boutique%20fitting%20room%20warm%20cream%20interior%20soft%20natural%20light%20quality%20fashion%20designer%20brand%20appointment%20styling%20consultation%20sophisticated%20ambiance%20wide%20shot&width=1400&height=600&seq=appt-hero&orientation=landscape"
            alt="預約試穿"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/50" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <p className="text-[#f5d8b8] text-[0.9375rem] tracking-[0.3em] uppercase font-medium mb-3">Fitting Appointment</p>
            <h1 className="text-white text-[2.75rem] md:text-[3.25rem] font-bold leading-[1.3] mb-3">預約試穿</h1>
            <p className="text-white/80 text-[1.125rem] max-w-lg leading-[1.75]">專屬造型顧問陪伴，輕鬆找到最適合您的質感穿搭。</p>
          </div>
        </section>

        {/* Info Strip */}
        <section className="bg-[#3d2b1f] text-[#faf8f5] py-5">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 text-center sm:text-left">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center text-[#c9a98a] text-xl"><i className="ri-map-pin-2-line"></i></div>
                <div>
                  <p className="text-[#c9a98a] text-xs font-medium tracking-wider uppercase">門市地址</p>
                  <p className="text-sm font-medium">台中市西區精誠路 123 號</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center text-[#c9a98a] text-xl"><i className="ri-time-line"></i></div>
                <div>
                  <p className="text-[#c9a98a] text-xs font-medium tracking-wider uppercase">營業時間</p>
                  <p className="text-sm font-medium">週一至週六 10:00–19:00（週日公休）</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center text-[#c9a98a] text-xl"><i className="ri-line-fill"></i></div>
                <div>
                  <p className="text-[#c9a98a] text-xs font-medium tracking-wider uppercase">LINE 確認</p>
                  <p className="text-sm font-medium">預約送出後由 LINE 客服確認時段</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Form Area */}
        <section className="py-16 md:py-24">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            {submitted ? (
              /* Success State */
              <div className="max-w-xl mx-auto text-center py-16">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#f5ede4] mx-auto mb-6">
                  <i className="ri-checkbox-circle-line text-[#8B5E52] text-4xl"></i>
                </div>
                <h2 className="text-[#3d2b1f] text-[2rem] font-bold mb-4">預約申請已送出！</h2>
                <p className="text-[#7a6455] text-[1.125rem] leading-[1.75] mb-3">
                  感謝您的預約，我們會盡快透過 LINE 與您確認試穿時段。
                </p>
                <div className="bg-[#faf0e8] rounded-lg px-6 py-5 mb-8 text-left space-y-2">
                  <p className="text-[#3d2b1f] font-semibold text-[1rem]">預約資訊摘要</p>
                  <p className="text-[#7a6455] text-[0.9375rem]">服務：{selectedService}</p>
                  <p className="text-[#7a6455] text-[0.9375rem]">日期：{selectedDate}</p>
                  <p className="text-[#7a6455] text-[0.9375rem]">時段：{selectedTime}</p>
                </div>
                <p className="text-[#a09080] text-[0.9375rem] mb-8 leading-relaxed">
                  若需更改時段或有任何問題，歡迎直接加入 LINE 聯繫我們的造型顧問。
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="https://line.me" target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-7 py-3 bg-[#3d2b1f] text-[#faf8f5] rounded-full font-semibold text-[1rem] hover:bg-[#8B5E52] transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-line-fill"></i> 加入 LINE 確認
                  </a>
                  <Link to="/shop"
                    className="flex items-center justify-center gap-2 px-7 py-3 border-2 border-[#3d2b1f] text-[#3d2b1f] rounded-full font-semibold text-[1rem] hover:bg-[#3d2b1f] hover:text-[#faf8f5] transition-colors cursor-pointer whitespace-nowrap">
                    瀏覽商品
                  </Link>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-10 xl:gap-16 items-start">
                {/* Form */}
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-8"
                >
                  {/* Step 1: Service */}
                  <div>
                    <h2 className="text-[#3d2b1f] text-[1.375rem] font-bold mb-1">Step 1｜選擇服務類型</h2>
                    <p className="text-[#a09080] text-[0.9375rem] mb-5">請選擇您希望預約的服務項目</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {services.map(s => (
                        <button
                          type="button"
                          key={s.id}
                          onClick={() => setSelectedService(s.label)}
                          className={`p-5 rounded-lg border-2 text-left transition-all cursor-pointer ${selectedService === s.label ? "border-[#8B5E52] bg-[#fdf5ee]" : "border-[#ede8e0] bg-white hover:border-[#c9a98a]"}`}
                        >
                          <p className={`font-bold text-[1rem] mb-1.5 ${selectedService === s.label ? "text-[#8B5E52]" : "text-[#3d2b1f]"}`}>{s.label}</p>
                          <p className="text-[#7a6455] text-[0.875rem] leading-snug">{s.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Step 2: Calendar */}
                  <div>
                    <h2 className="text-[#3d2b1f] text-[1.375rem] font-bold mb-1">Step 2｜選擇預約日期</h2>
                    <p className="text-[#a09080] text-[0.9375rem] mb-5">週日公休，請選擇週一至週六</p>
                    <div className="bg-white rounded-lg p-6 max-w-sm border border-[#ede8e0]">
                      {/* Month Nav */}
                      <div className="flex items-center justify-between mb-5">
                        <button type="button" onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f5ede4] transition-colors cursor-pointer text-[#3d2b1f]">
                          <i className="ri-arrow-left-s-line text-xl"></i>
                        </button>
                        <span className="text-[#3d2b1f] font-bold text-[1.0625rem]">{currentYear} 年 {monthNames[currentMonth]}</span>
                        <button type="button" onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f5ede4] transition-colors cursor-pointer text-[#3d2b1f]">
                          <i className="ri-arrow-right-s-line text-xl"></i>
                        </button>
                      </div>
                      {/* Weekday headers */}
                      <div className="grid grid-cols-7 mb-2">
                        {weekDays.map(d => (
                          <div key={d} className={`text-center text-[0.8125rem] font-semibold py-1 ${d === "日" ? "text-[#c9a98a]" : "text-[#a09080]"}`}>{d}</div>
                        ))}
                      </div>
                      {/* Days */}
                      <div className="grid grid-cols-7 gap-y-1">
                        {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                          const day = i + 1;
                          const disabled = isBeforeToday(day) || isSunday(day);
                          const dateStr = formatDate(day);
                          const selected = selectedDate === dateStr;
                          return (
                            <button
                              type="button"
                              key={day}
                              disabled={disabled}
                              onClick={() => !disabled && setSelectedDate(dateStr)}
                              className={`w-full aspect-square flex items-center justify-center rounded-full text-[0.9375rem] font-medium transition-all cursor-pointer
                                ${disabled ? "text-[#d8cfc7] cursor-not-allowed" : selected ? "bg-[#3d2b1f] text-white" : "text-[#3d2b1f] hover:bg-[#f5ede4]"}`}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {selectedDate && (
                      <p className="mt-3 text-[#8B5E52] text-[0.9375rem] font-medium flex items-center gap-1.5">
                        <i className="ri-calendar-check-line"></i> 已選擇：{selectedDate}
                      </p>
                    )}
                  </div>

                  {/* Step 3: Time Slot */}
                  <div>
                    <h2 className="text-[#3d2b1f] text-[1.375rem] font-bold mb-1">Step 3｜選擇時段</h2>
                    <p className="text-[#a09080] text-[0.9375rem] mb-5">每次試穿約 30 至 60 分鐘</p>
                    <div className="flex flex-wrap gap-3">
                      {timeSlots.map(t => (
                        <button
                          type="button"
                          key={t}
                          onClick={() => setSelectedTime(t)}
                          className={`px-5 py-2.5 rounded-full border-2 text-[0.9375rem] font-medium transition-all cursor-pointer whitespace-nowrap
                            ${selectedTime === t ? "bg-[#3d2b1f] text-white border-[#3d2b1f]" : "bg-white text-[#3d2b1f] border-[#ede8e0] hover:border-[#8B5E52]"}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Step 4: Contact Info */}
                  <div>
                    <h2 className="text-[#3d2b1f] text-[1.375rem] font-bold mb-1">Step 4｜填寫聯絡資料</h2>
                    <p className="text-[#a09080] text-[0.9375rem] mb-5">預約確認通知將透過 LINE 或電話告知</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[#3d2b1f] text-[0.9375rem] font-semibold mb-2">姓名 <span className="text-[#8B5E52]">*</span></label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="請輸入您的姓名"
                          className="w-full border border-[#ddd5ca] rounded-lg px-4 py-3 text-[0.9375rem] text-[#3d2b1f] placeholder-[#c0b5ab] focus:outline-none focus:border-[#8B5E52] transition-colors bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[#3d2b1f] text-[0.9375rem] font-semibold mb-2">手機號碼 <span className="text-[#8B5E52]">*</span></label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="0912-345-678"
                          className="w-full border border-[#ddd5ca] rounded-lg px-4 py-3 text-[0.9375rem] text-[#3d2b1f] placeholder-[#c0b5ab] focus:outline-none focus:border-[#8B5E52] transition-colors bg-white"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-[#3d2b1f] text-[0.9375rem] font-semibold mb-2">電子信箱</label>
                        <input
                          type="email"
                          name="email"
                          placeholder="your@email.com"
                          className="w-full border border-[#ddd5ca] rounded-lg px-4 py-3 text-[0.9375rem] text-[#3d2b1f] placeholder-[#c0b5ab] focus:outline-none focus:border-[#8B5E52] transition-colors bg-white"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-[#3d2b1f] text-[0.9375rem] font-semibold mb-2">備註說明</label>
                        <textarea
                          name="note"
                          rows={3}
                          maxLength={500}
                          placeholder="例如：希望諮詢特定場合穿搭、有喜歡的款式或顏色偏好..."
                          className="w-full border border-[#ddd5ca] rounded-lg px-4 py-3 text-[0.9375rem] text-[#3d2b1f] placeholder-[#c0b5ab] focus:outline-none focus:border-[#8B5E52] transition-colors bg-white resize-none"
                        />
                        <p className="text-[#a09080] text-xs mt-1">最多 500 字</p>
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-[#8B5E52] bg-[#fdf5ee] border border-[#e8c9b0] rounded-lg px-4 py-3 text-[0.9375rem]">
                      <i className="ri-error-warning-line text-lg"></i> {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto px-10 py-4 bg-[#3d2b1f] text-[#faf8f5] rounded-full text-[1.0625rem] font-bold hover:bg-[#8B5E52] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "送出中…" : "確認送出預約申請"}
                  </button>
                  <p className="text-[#a09080] text-[0.875rem] mt-2">送出後，我們將在 1 個工作天內透過 LINE 確認預約時段。</p>
                </form>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* LINE CTA */}
                  <div className="bg-[#3d2b1f] text-[#faf8f5] rounded-lg p-7">
                    <div className="w-10 h-10 flex items-center justify-center text-[#c9a98a] text-2xl mb-4"><i className="ri-line-fill"></i></div>
                    <h3 className="text-[1.125rem] font-bold mb-2">直接加 LINE 預約更快！</h3>
                    <p className="text-[#c9a98a] text-[0.9375rem] leading-[1.7] mb-5">若需立即確認時段，或想了解目前商品庫存，歡迎直接加入 LINE 官方帳號，由造型顧問即時回覆。</p>
                    <a href="https://line.me" target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3 bg-[#faf8f5] text-[#3d2b1f] rounded-full font-bold text-[0.9375rem] hover:bg-[#f5ede4] transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-line-fill"></i> 加入 LINE 立即詢問
                    </a>
                  </div>

                  {/* Notice */}
                  <div className="bg-white rounded-lg p-6 border border-[#ede8e0]">
                    <h3 className="text-[#3d2b1f] text-[1rem] font-bold mb-4 flex items-center gap-2">
                      <i className="ri-information-line text-[#8B5E52]"></i> 預約須知
                    </h3>
                    <ul className="space-y-3 text-[#7a6455] text-[0.9375rem] leading-[1.7]">
                      <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-[#8B5E52] mt-0.5 flex-shrink-0"></i>預約完成後，將由 LINE 客服於 1 個工作天內確認時段。</li>
                      <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-[#8B5E52] mt-0.5 flex-shrink-0"></i>如需取消或更改預約，請提前 24 小時透過 LINE 告知。</li>
                      <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-[#8B5E52] mt-0.5 flex-shrink-0"></i>週日為公休日，不提供試穿服務。</li>
                      <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-[#8B5E52] mt-0.5 flex-shrink-0"></i>每次試穿服務時間約 30–60 分鐘，請預留充裕時間。</li>
                      <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-[#8B5E52] mt-0.5 flex-shrink-0"></i>建議穿著方便更換的服裝前來試穿。</li>
                    </ul>
                  </div>

                  {/* Store Photo */}
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=elegant%20fashion%20boutique%20interior%20warm%20cream%20ivory%20soft%20lighting%20wooden%20furniture%20designer%20clothes%20rack%20quality%20taiwan%20style%20sophisticated%20minimal&width=420&height=280&seq=appt-store&orientation=landscape"
                      alt="門市環境"
                      className="w-full h-[200px] object-cover object-top"
                    />
                    <div className="bg-white p-4 border border-t-0 border-[#ede8e0] rounded-b-lg">
                      <p className="text-[#3d2b1f] font-semibold text-[0.9375rem]">台中門市</p>
                      <p className="text-[#7a6455] text-[0.875rem] mt-1">台中市西區精誠路 123 號｜週一至週六 10:00–19:00</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
